import { useEffect, useMemo, useState } from "react";
import { api, ApiError, PromoRow } from "../api/client";
import { useToast } from "../ui/toast";
import { IconCopy, IconPlus, IconRefresh, IconSearch, IconX } from "../ui/icons";
import { DataTable, type Column } from "../ui/DataTable";
import { fmtDate } from "../ui/licenseStatus";

const MAX_PERCENT = 90; // backend enforces the same cap

/** Same rule as the backend: usable now? */
function promoState(p: PromoRow): { key: string; label: string } {
  if (!p.active) return { key: "revoked", label: "Wyłączony" };
  if (p.validUntil && Date.now() > new Date(`${p.validUntil}T23:59:59.999Z`).getTime()) return { key: "expired", label: "Wygasł" };
  if (p.maxUses && p.uses >= p.maxUses) return { key: "expiring", label: "Wykorzystany" };
  return { key: "active", label: "Aktywny" };
}

/**
 * Promo codes: generate a code (random or your own, e.g. JESIEN20) with a % discount, an
 * optional expiry date and use limit. The portal checkout accepts the code and recomputes the
 * price; a use is counted only when the order is PAID.
 */
export function Promos() {
  const toast = useToast();
  const [rows, setRows] = useState<PromoRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [busyCode, setBusyCode] = useState<string | null>(null);

  const load = () => {
    setRows(null);
    setError(null);
    api.listPromos()
      .then((r) => setRows(r.promos))
      .catch((e: ApiError) => setError(e.status === 403 ? "Brak dostępu (owner-only)." : e.message));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const q = query.trim().toUpperCase();
    return (rows ?? []).filter((p) => !q || p.code.includes(q) || (p.note ?? "").toUpperCase().includes(q));
  }, [rows, query]);

  const kpis = useMemo(() => {
    const all = rows ?? [];
    return {
      active: all.filter((p) => promoState(p).key === "active").length,
      uses: all.reduce((s, p) => s + (p.uses ?? 0), 0),
    };
  }, [rows]);

  const toggle = async (p: PromoRow) => {
    setBusyCode(p.code);
    try {
      await api.updatePromo(p.code, { active: !p.active });
      toast(p.active ? `Kod ${p.code} wyłączony` : `Kod ${p.code} włączony`);
      load();
    } catch (e) { toast((e as ApiError).message); } finally { setBusyCode(null); }
  };

  const remove = async (p: PromoRow) => {
    if (!window.confirm(`Usunąć kod ${p.code}? Opłacone zamówienia zachowają informację o rabacie.`)) return;
    setBusyCode(p.code);
    try {
      await api.deletePromo(p.code);
      toast(`Usunięto kod ${p.code}`);
      load();
    } catch (e) { toast((e as ApiError).message); } finally { setBusyCode(null); }
  };

  const columns: Column<PromoRow>[] = [
    {
      key: "code", label: "Kod", filter: "none", get: (p) => p.code,
      render: (p) => (
        <span className="row" style={{ gap: 6 }}>
          <span className="mono" style={{ fontWeight: 600 }}>{p.code}</span>
          <button className="btn icon sm ghost" title="Kopiuj kod"
            onClick={() => { navigator.clipboard.writeText(p.code); toast(`Skopiowano ${p.code}`); }}>
            <IconCopy size={13} />
          </button>
        </span>
      ),
    },
    { key: "percent", label: "Rabat", align: "right", filter: "none", get: (p) => p.percent, render: (p) => <b className="tabular">−{p.percent}%</b> },
    {
      key: "state", label: "Status", get: (p) => promoState(p).label,
      render: (p) => { const s = promoState(p); return <span className={`badge st-${s.key}`}><span className={`dot dot-${s.key}`} />{s.label}</span>; },
    },
    { key: "uses", label: "Użycia", align: "right", filter: "none", get: (p) => p.uses ?? 0, render: (p) => <span className="tabular">{p.uses ?? 0}{p.maxUses ? ` / ${p.maxUses}` : ""}</span> },
    { key: "valid", label: "Ważny do", align: "right", filter: "none", get: (p) => p.validUntil ?? "9999", render: (p) => <span className="tabular">{p.validUntil ?? "bez limitu"}</span> },
    { key: "note", label: "Notatka", filter: "none", get: (p) => p.note ?? "", render: (p) => <span className="muted">{p.note || "—"}</span> },
    { key: "created", label: "Utworzono", align: "right", filter: "date", get: (p) => new Date(p.createdAt).getTime(), render: (p) => <span className="tabular">{fmtDate(p.createdAt)}</span> },
    {
      key: "actions", label: "", filter: "none", get: () => "",
      render: (p) => (
        <span className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
          <button className="btn sm" disabled={busyCode === p.code} onClick={() => toggle(p)}>{p.active ? "Wyłącz" : "Włącz"}</button>
          <button className="btn sm danger" disabled={busyCode === p.code} onClick={() => remove(p)}>Usuń</button>
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="kpi-grid" style={{ marginBottom: 22 }}>
        <div className="card kpi"><div className="kpi-label">Aktywne kody</div><div className="kpi-value" style={{ color: "var(--accent-text)" }}>{kpis.active}</div></div>
        <div className="card kpi"><div className="kpi-label">Opłacone zamówienia z kodem</div><div className="kpi-value">{kpis.uses}</div></div>
      </div>

      <div className="row" style={{ marginBottom: 14 }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 360 }}>
          <span style={{ position: "absolute", left: 11, top: 10, color: "var(--text-faint)" }}><IconSearch size={15} /></span>
          <input className="input" style={{ paddingLeft: 34 }} placeholder="Szukaj: kod / notatka…"
            value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="topbar-spacer" />
        <button className="btn" onClick={load}><IconRefresh size={15} /> Odśwież</button>
        <button className="btn primary" onClick={() => setShowModal(true)}><IconPlus size={15} /> Nowy kod</button>
      </div>

      {error ? (
        <div className="card"><div className="empty">{error}</div></div>
      ) : !rows ? (
        <div className="card"><div className="empty">Ładowanie…</div></div>
      ) : (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(p) => p.code}
          initialSort={{ key: "created", dir: "desc" }}
          empty={rows.length === 0 ? "Brak kodów. Kliknij „Nowy kod”, aby utworzyć pierwszy." : "Brak wyników dla filtrów."}
        />
      )}

      {showModal && <NewPromoModal onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); load(); }} />}
    </>
  );
}

function NewPromoModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const [mode, setMode] = useState<"random" | "custom">("random");
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState(10);
  const [validUntil, setValidUntil] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<PromoRow | null>(null);

  const percentOk = Number.isInteger(percent) && percent >= 1 && percent <= MAX_PERCENT;
  const codeOk = mode === "random" || /^[A-Z0-9][A-Z0-9_-]{2,31}$/.test(code.trim().toUpperCase());

  const create = async () => {
    if (!percentOk) { toast(`Rabat: od 1 do ${MAX_PERCENT}%`); return; }
    if (!codeOk) { toast("Kod: 3–32 znaki, litery A–Z, cyfry, „-” lub „_”"); return; }
    setBusy(true);
    try {
      const r = await api.createPromo({
        ...(mode === "custom" ? { code: code.trim().toUpperCase() } : {}),
        percent,
        ...(validUntil ? { validUntil } : {}),
        ...(maxUses ? { maxUses: Number(maxUses) } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setCreated(r.promo);
    } catch (e) { toast((e as ApiError).message); } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ padding: "18px 22px", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ fontSize: 16 }}>{created ? "Kod utworzony" : "Nowy kod promocyjny"}</h3>
          <div className="topbar-spacer" />
          <button className="btn icon sm ghost" onClick={onClose}><IconX size={16} /></button>
        </div>

        <div style={{ padding: 22 }}>
          {!created ? (
            <>
              <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
                Kod obniża cenę zakupu w portalu o podany procent — dla każdego okresu (1, 3 i 12 miesięcy).
                Użycie liczy się dopiero po opłaceniu zamówienia. Maksymalny rabat: {MAX_PERCENT}%.
              </p>
              <div className="field">
                <label className="label">Kod</label>
                <div className="seg" style={{ marginBottom: 8 }}>
                  <button className={mode === "random" ? "active" : ""} onClick={() => setMode("random")}>Wygeneruj losowy</button>
                  <button className={mode === "custom" ? "active" : ""} onClick={() => setMode("custom")}>Własny</button>
                </div>
                {mode === "custom" && (
                  <input className="input mono" value={code} autoFocus placeholder="np. JESIEN20"
                    onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s+/g, ""))} />
                )}
              </div>
              <div className="grid-2">
                <div className="field">
                  <label className="label">Rabat (%)</label>
                  <input className="input" type="number" min={1} max={MAX_PERCENT} step={1} value={percent}
                    onChange={(e) => setPercent(Math.trunc(Number(e.target.value)))} />
                </div>
                <div className="field">
                  <label className="label">Limit użyć (opcjonalnie)</label>
                  <input className="input" type="number" min={1} step={1} value={maxUses} placeholder="bez limitu"
                    onChange={(e) => setMaxUses(e.target.value)} />
                </div>
              </div>
              <div className="grid-2">
                <div className="field">
                  <label className="label">Ważny do (opcjonalnie, włącznie)</label>
                  <input className="input" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
                </div>
                <div className="field">
                  <label className="label">Notatka (opcjonalnie)</label>
                  <input className="input" value={note} maxLength={200} placeholder="np. newsletter listopad"
                    onChange={(e) => setNote(e.target.value)} />
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
                Kod jest aktywny — możesz go wysłać w newsletterze lub opublikować.
              </p>
              <div className="result-block" style={{ fontSize: 20, textAlign: "center", letterSpacing: "0.08em" }}>{created.code}</div>
              <p className="faint" style={{ fontSize: 12, marginTop: 8, textAlign: "center" }}>
                −{created.percent}%{created.validUntil ? ` · ważny do ${created.validUntil}` : ""}{created.maxUses ? ` · limit ${created.maxUses} użyć` : ""}
              </p>
              <button className="btn primary" style={{ marginTop: 14, width: "100%" }}
                onClick={() => { navigator.clipboard.writeText(created.code); toast("Skopiowano kod"); }}>
                <IconCopy size={15} /> Kopiuj kod
              </button>
            </>
          )}
        </div>

        <div className="row" style={{ padding: "16px 22px", borderTop: "1px solid var(--border)", justifyContent: "flex-end" }}>
          {!created ? (
            <>
              <button className="btn ghost" onClick={onClose}>Anuluj</button>
              <button className="btn primary" disabled={busy || !percentOk || !codeOk} onClick={create}>{busy ? "Tworzę…" : "Utwórz kod"}</button>
            </>
          ) : (
            <button className="btn primary" onClick={onCreated}>Gotowe</button>
          )}
        </div>
      </div>
    </div>
  );
}
